import React, { useCallback, useState } from "react";
import { Modal, Button, Form, Input } from "antd";
import { useTranslation } from "react-i18next";
import axios, { AxiosError, AxiosResponse } from "axios";
import * as Yup from "yup";
import api from "../../../services/api";
import { IActionModalProps } from "..";
import { useToast } from "../../../hooks/toast";
import FormItem from "../../FormItem";
import getValidationErrors from "../../../utils/getValidationErrors";

interface ISuspendModalFormData {
  reason: string;
}

interface IApprovalData {
  status: string;
  reason: string;
}

const layout = {
  labelCol: { span: 4 },
  wrapperCol: { span: 20 },
};

const SuspendModal: React.FC<IActionModalProps> = ({
  project,
  handleUpdateProject,
}) => {
  const { t } = useTranslation(["project-actions"]);
  const [visible, setVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState({});

  const [form] = Form.useForm();

  const { addToast } = useToast();

  const handleClose = useCallback(() => {
    setVisible(false);
  }, []);

  const generateErrorToast = useCallback(
    (response: AxiosResponse | undefined) => {
      let description;

      switch (response?.status) {
        case 404:
          description = "O projeto não foi encontrado";
          break;
        default:
          description =
            "Um erro occoreu ao suspender o projeto, tente novamente";
      }

      addToast({
        type: "error",
        title: "Erro ao suspender",
        description,
      });
    },
    [addToast]
  );

  const handleSendRequest = useCallback(
    async (payload: IApprovalData) => {
      try {
        setLoading(true);

        const response = await api.patch(
          `/projects/${project.id}/cancel-suspend`,
          payload
        );

        handleUpdateProject(response.data);
        addToast({
          type: "success",
          title: "Projeto suspenso",
          description: "O projeto foi suspenso com sucesso",
        });
      } catch (err) {
        if (axios.isAxiosError(err)) {
          const serverError = err as AxiosError;
          generateErrorToast(serverError.response);
        } else {
          console.log(err);
        }
      } finally {
        setLoading(false);
      }
    },
    [addToast, generateErrorToast, handleUpdateProject, project.id]
  );

  const handleSubmit = useCallback(
    async (data: ISuspendModalFormData) => {
      try {
        const schema = Yup.object().shape({
          reason: Yup.string().required("Motivo obrigatório"),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        const { reason } = data;

        const payload = {
          status: "SUSPENDED",
          reason,
        };

        handleSendRequest(payload);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          setValidationErrors(errors);
        }
      }
    },
    [handleSendRequest]
  );

  return (
    <>
      <Modal
        title={t("suspend")}
        visible={visible}
        onOk={() => form.submit()}
        onCancel={() => handleClose()}
        okButtonProps={{ htmlType: "submit", loading }}
        getContainer={false}
        forceRender
      >
        <Form
          {...layout}
          form={form}
          name="approve-form"
          onFinish={handleSubmit}
        >
          <FormItem
            name="reason"
            required
            label={t("reason")}
            errors={validationErrors}
          >
            <Input />
          </FormItem>
        </Form>
      </Modal>
      <Button
        type="primary"
        danger
        onClick={() => setVisible((oldVisible) => !oldVisible)}
      >
        {t("suspend")}
      </Button>
    </>
  );
};

export default SuspendModal;
